/**
 * Typed client for the per-account buyer-detail retention setting
 * (ADR-0065 §8):
 *
 *   GET /v1/account/buyer-detail-retention  — owner/admin reads the
 *                                              current value.
 *   PUT /v1/account/buyer-detail-retention  — owner/admin writes a new
 *                                              value. Per-account 10/min
 *                                              rate limit on the server.
 *
 * Wire body is `{ setting: '30_days' | '90_days' | 'tie_to_retention_class' }`
 * — **snake_case** per CLAUDE.md §3. The change applies to NEWLY captured
 * transactions only (the server computes `expires_at` at INSERT time
 * from the per-account setting); existing rows are NOT retroactively
 * shortened. The dashboard surfaces this in helper copy.
 */

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

/**
 * The three retention options the merchant can pick. Default is
 * `'90_days'` (the safe posture for typical dispute-review windows;
 * ADR-0065 §"Fifth").
 */
export type BuyerDetailRetentionSetting =
  | '30_days'
  | '90_days'
  | 'tie_to_retention_class';

/**
 * GET result discriminated union.
 *   - `ok`        — server returned 200 with a valid enum.
 *   - `forbidden` — caller's role is reviewer/viewer (server 403).
 *   - `error`     — network / 5xx / bad shape.
 */
export type GetRetentionResult =
  | { readonly kind: 'ok'; readonly setting: BuyerDetailRetentionSetting }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * PUT result discriminated union.
 *   - `ok`              — server accepted; echoes the new value.
 *   - `forbidden`       — role gate (server 403).
 *   - `bad_request`     — enum validation failed (server 400).
 *   - `rate_limited`    — 10/min/account ceiling hit.
 *   - `error`           — network / 5xx / bad shape.
 */
export type PutRetentionResult =
  | { readonly kind: 'ok'; readonly setting: BuyerDetailRetentionSetting }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'bad_request' }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/* ---------- internal wire shape ---------- */

interface RetentionWire {
  readonly setting: string;
}

/* --------------------------------------------------------------------- */

/**
 * GET /v1/account/buyer-detail-retention.
 */
export async function getBuyerDetailRetention(
  http: HttpClient,
): Promise<GetRetentionResult> {
  try {
    const res = await http.request<RetentionWire>({
      method: 'GET',
      path: '/v1/account/buyer-detail-retention',
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const setting = parseSetting(res.setting);
    if (setting === null) {
      return { kind: 'error', message: 'Server returned an unknown setting value.' };
    }
    return { kind: 'ok', setting };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/**
 * PUT /v1/account/buyer-detail-retention.
 *
 * Body is `{ setting }`. The server returns 200 with the new value on
 * success, 400 on enum validation failure, 403 on role gate, 429 on
 * per-account rate limit.
 */
export async function putBuyerDetailRetention(
  http: HttpClient,
  setting: BuyerDetailRetentionSetting,
): Promise<PutRetentionResult> {
  try {
    const res = await http.request<RetentionWire>({
      method: 'PUT',
      path: '/v1/account/buyer-detail-retention',
      body: { setting },
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const echoed = parseSetting(res.setting);
    if (echoed === null) {
      return { kind: 'error', message: 'Server returned an unknown setting value.' };
    }
    return { kind: 'ok', setting: echoed };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'bad_request') return { kind: 'bad_request' };
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'rate_limited',
        message:
          "You've changed this setting too many times. Wait a minute and try again.",
      };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/* ---------- helpers ---------- */

function parseSetting(raw: string | undefined): BuyerDetailRetentionSetting | null {
  switch (raw) {
    case '30_days':
    case '90_days':
    case 'tie_to_retention_class':
      return raw;
    default:
      return null;
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Retention setting service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
